import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { 
  FileText, 
  RefreshCw, 
  LogIn, 
  LogOut, 
  Plus, 
  Edit, 
  Trash2,
  CreditCard,
  User,
  ShoppingCart,
  Calendar,
  Filter,
  Activity
} from 'lucide-react';

const AuditLogsPage = () => {
  const { API_URL, getAuthHeader } = useAuth();
  const { t } = useLanguage();
  
  const [logs, setLogs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    action: '',
    startDate: '',
    endDate: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.action) params.append('action', filters.action);
      if (filters.startDate) params.append('start_date', filters.startDate);
      if (filters.endDate) params.append('end_date', filters.endDate);
      
      const [logsRes, summaryRes] = await Promise.all([
        axios.get(`${API_URL}/api/audit-logs?${params.toString()}`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/audit-logs/summary`, { headers: getAuthHeader() })
      ]);
      
      setLogs(logsRes.data);
      setSummary(summaryRes.data);
    } catch (error) {
      console.error('Error fetching audit logs:', error);
      toast.error('حدث خطأ في تحميل سجل التدقيق');
    } finally {
      setLoading(false);
    }
  };

  const handleFilter = () => {
    fetchData();
  };

  const clearFilters = () => {
    setFilters({ action: '', startDate: '', endDate: '' });
    setTimeout(fetchData, 100);
  };

  const getActionIcon = (action) => {
    const icons = {
      login: <LogIn className="w-4 h-4 text-green-600" />,
      logout: <LogOut className="w-4 h-4 text-red-600" />,
      create: <Plus className="w-4 h-4 text-blue-600" />,
      update: <Edit className="w-4 h-4 text-amber-600" />,
      delete: <Trash2 className="w-4 h-4 text-red-600" />,
      payment: <CreditCard className="w-4 h-4 text-green-600" />,
      order: <ShoppingCart className="w-4 h-4 text-purple-600" />
    };
    return icons[action] || <Activity className="w-4 h-4 text-stone-500" />;
  };

  const getActionLabel = (action) => {
    const labels = {
      login: 'تسجيل دخول',
      logout: 'تسجيل خروج',
      create: 'إنشاء',
      update: 'تعديل',
      delete: 'حذف',
      payment: 'دفعة',
      order: 'طلب'
    };
    return labels[action] || action;
  };

  const getActionColor = (action) => {
    const colors = {
      login: 'bg-green-100 text-green-700',
      logout: 'bg-red-100 text-red-700',
      create: 'bg-blue-100 text-blue-700',
      update: 'bg-amber-100 text-amber-700',
      delete: 'bg-red-100 text-red-700',
      payment: 'bg-emerald-100 text-emerald-700',
      order: 'bg-purple-100 text-purple-700'
    };
    return colors[action] || 'bg-stone-100 text-stone-700';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-amber-600 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <FileText className="w-8 h-8 text-amber-600" />
          <div>
            <h1 className="text-2xl font-bold text-stone-800">سجل التدقيق</h1>
            <p className="text-stone-500 text-sm">تتبع جميع العمليات في النظام</p>
          </div>
        </div>
        <Button onClick={fetchData} variant="outline" className="gap-2">
          <RefreshCw className="w-4 h-4" />
          تحديث
        </Button>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
            <CardContent className="p-4 text-center">
              <FileText className="w-8 h-8 mx-auto mb-2 opacity-80" />
              <p className="text-3xl font-bold">{summary.total_logs || 0}</p>
              <p className="text-sm opacity-90">إجمالي السجلات</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
            <CardContent className="p-4 text-center">
              <Activity className="w-8 h-8 mx-auto mb-2 opacity-80" />
              <p className="text-3xl font-bold">{summary.today_activity || 0}</p>
              <p className="text-sm opacity-90">نشاط اليوم</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white">
            <CardContent className="p-4 text-center">
              <LogIn className="w-8 h-8 mx-auto mb-2 opacity-80" />
              <p className="text-3xl font-bold">{summary.by_action?.login || 0}</p>
              <p className="text-sm opacity-90">تسجيلات الدخول</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white">
            <CardContent className="p-4 text-center">
              <Edit className="w-8 h-8 mx-auto mb-2 opacity-80" />
              <p className="text-3xl font-bold">{(summary.by_action?.create || 0) + (summary.by_action?.update || 0)}</p>
              <p className="text-sm opacity-90">إنشاء وتعديل</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Filter className="w-5 h-5 text-amber-600" />
            تصفية السجلات
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label>نوع العملية</Label>
              <Select value={filters.action || 'all'} onValueChange={(v) => setFilters({...filters, action: v === 'all' ? '' : v})}>
                <SelectTrigger>
                  <SelectValue placeholder="الكل" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">الكل</SelectItem>
                  <SelectItem value="login">تسجيل دخول</SelectItem>
                  <SelectItem value="logout">تسجيل خروج</SelectItem>
                  <SelectItem value="create">إنشاء</SelectItem>
                  <SelectItem value="update">تعديل</SelectItem>
                  <SelectItem value="delete">حذف</SelectItem>
                  <SelectItem value="payment">دفعة</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>من تاريخ</Label>
              <Input 
                type="date" 
                value={filters.startDate}
                onChange={(e) => setFilters({...filters, startDate: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label>إلى تاريخ</Label>
              <Input 
                type="date" 
                value={filters.endDate}
                onChange={(e) => setFilters({...filters, endDate: e.target.value})}
              />
            </div>
            <div className="flex items-end gap-2">
              <Button onClick={handleFilter} className="bg-amber-600 hover:bg-amber-700">
                تطبيق الفلتر
              </Button>
              <Button onClick={clearFilters} variant="outline">
                مسح
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Logs Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-amber-600" />
            السجلات ({logs.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {logs.length === 0 ? (
            <div className="text-center py-12 text-stone-500">
              <FileText className="w-16 h-16 mx-auto mb-4 opacity-30" />
              <p className="text-lg">لا توجد سجلات</p>
              <p className="text-sm mt-2">ستظهر هنا جميع العمليات المسجلة</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-right">التاريخ والوقت</TableHead>
                    <TableHead className="text-right">المستخدم</TableHead>
                    <TableHead className="text-center">العملية</TableHead>
                    <TableHead className="text-right">التفاصيل</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.map((log) => (
                    <TableRow key={log.id}>
                      <TableCell className="text-sm">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-stone-400" />
                          {new Date(log.created_at).toLocaleString('ar')}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-stone-400" />
                          <span className="font-medium">{log.user_name}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getActionColor(log.action)}`}>
                          {getActionIcon(log.action)}
                          {getActionLabel(log.action)}
                        </span>
                      </TableCell>
                      <TableCell className="text-stone-600">
                        {log.details}
                        {log.entity_type && (
                          <span className="text-stone-400 text-xs ms-2">
                            ({log.entity_type})
                          </span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AuditLogsPage;
